import {Link} from "@/components/atoms/link";
import {
  deserialize,
  DrupalNode, DrupalParagraph,
} from 'next-drupal';

import { useRouter } from 'next/router';
import {useEffect, useState, createRef, useContext} from "react";
import {searchIndex} from "lib/search-index";
import {DrupalJsonApiParams} from "drupal-jsonapi-params";
import {Dictionary, DrupalProductNode} from "types/hygiena-types";
import {SearchHero} from "@/components/organisms/hero/search-hero";
import {getKvpPair} from "@/lib/get-kvp-pair";
import {FormattedText} from "@/components/formatted-text";

import dynamic from "next/dynamic";
import {DictionaryContext} from "@/context/dictionary-context";
import striptags from "striptags";

const LoadingSpinnerOverlay = dynamic(() => import("../../atoms/loading-spinner").then((mod) => mod.LoadingSpinnerOverlay));
const MediaImage = dynamic(() => import("components/templates/media/media--image").then((mod) => mod.MediaImage));
const Button = dynamic(() => import("components/atoms/button").then((mod) => mod.Button));
const Pagination = dynamic(() => import("components/organisms/widget--pagination").then((mod) => mod.Pagination), {ssr: false});
const TruncateMarkup= dynamic(() => import("components/atoms/truncate-markup").then((mod) => mod.TruncateMarkup));

const params = {
  filter: {},
}

interface SearchPageProps {
  node: DrupalNode,
}

export function SearchPage({node}: SearchPageProps) {
  const router = useRouter();
  const scrollRef = createRef<HTMLDivElement>();
  const t = useContext<Dictionary>(DictionaryContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageLimit, setPageLimit] = useState(10);
  const [landingPagePage, setLandingPagePage] = useState(0);
  const [articlePage, setArticlePage] = useState(0);
  const [eventPage, setEventPage] = useState(0);
  const [productPage, setProductPage] = useState(0);
  const [productResults, setProductResults] = useState<DrupalNode[]>([])
  const [articleResults, setArticleResults] = useState<DrupalNode[]>([])
  const [eventResults, setEventResults] = useState<DrupalNode[]>([])
  const [landingPageResults, setLandingPageResults] = useState<DrupalNode[]>([])
  const [productResultCount, setProductResultCount] = useState(0);
  const [articleResultCount, setArticleResultCount] = useState(0);
  const [eventResultCount, setEventResultCount] = useState(0);
  const [landingPageResultCount, setLandingPageResultCount] = useState(0);

  const heroHeadline = getKvpPair(node, "hero_headline", "long")?.value;
  const searchPlaceholder = getKvpPair(node, "search_placeholder")?.value;
  const resultsForText = getKvpPair(node, "results_for")?.value;
  const resultsFoundText = getKvpPair(node, "results_found")?.value;
  const resultsText = t?.search?.results;
  const singleResultText = t?.search?.result;
  const productsHeaderText = t?.search?.products_header;
  const landingPageHeaderText = t?.search?.pages_header;
  const eventsHeaderText = t?.search?.events_header;
  const newsHeaderText = t?.search?.news_header;

  async function scrollToRef(ref) {
    if (typeof ref === "string") {
      const element = document.getElementById(ref);
      if (element) {
        element.scrollIntoView({behavior: 'smooth'})
      }
    }
    if (!ref?.current?.scrollIntoView) return;
    const current = ref.current as HTMLDivElement;
    current.scrollIntoView({behavior: 'smooth'})
  }

  async function updateSearch(query) {
    const articleParams = new DrupalJsonApiParams()
      .addInclude(["field_article_image.image", "field_article_image.bf_image"])
      .addFilter("language_with_fallback", router?.locale ?? "en", "=")
      .addFilter("fulltext", query)
      .addFilter("status", "1","=")
      .addPageLimit(pageLimit)
      .addPageOffset(articlePage ? pageLimit * articlePage : articlePage)
      .addSort("created", "DESC")
      .getQueryString();

    const productParams = new DrupalJsonApiParams()
      .addInclude([
        "field_product_image.image",
        "field_product_image.bf_image",
        "field_product_brand",
        "field_product_vars",
      ])
      .addFields("node--product", ["field_product_image", "field_product_brand", "field_product_vars", "path", "body", "title"])
      .addFilter("language_with_fallback", router?.locale ?? "en", "=")
      .addFilter("fulltext", query)
      .addFilter("status", "1","=")
      .addPageLimit(pageLimit)
      .addPageOffset(productPage ? pageLimit * productPage : productPage)
      .getQueryString();

    const eventsParams = new DrupalJsonApiParams()
      .addInclude(["field_event_image.image", "field_event_image.bf_image"])
      .addInclude(["field_event_hero_image.image", "field_event_hero_image.bf_image"])
      .addFilter("language_with_fallback", router?.locale ?? "en", "=")
      .addFilter("fulltext", query)
      .addFilter("status", "1","=")
      .addPageLimit(pageLimit)
      .addPageOffset(eventPage ? pageLimit * eventPage : eventPage)
      .addSort("created", "DESC")
      .getQueryString();

    const landingPageParams: any = new DrupalJsonApiParams()
      .addInclude([
        "field_paragraphs.field_hero_image.bf_image",
        "field_paragraphs.field_hero_image.image",
        "field_paragraphs.field_image_block_image.bf_image",
        "field_paragraphs.field_image_block_image.image",
      ])
      .addFilter("language_with_fallback", router?.locale ?? "en", "=")
      .addFilter("fulltext", query)
      .addFilter("status", "1","=")
      .addPageLimit(pageLimit)
      .addPageOffset(landingPagePage ? pageLimit * landingPagePage : landingPagePage)
      .addSort("created", "DESC")
      .getQueryString();

    const articleResponse = await searchIndex("articles", articleParams, router.locale, router.defaultLocale);
    const productResponse = await searchIndex("products", productParams, router.locale, router.defaultLocale);
    const eventResponse = await searchIndex("events", eventsParams, router.locale, router.defaultLocale);
    const landingPageResponse = await searchIndex("landing_page", landingPageParams, router.locale, router.defaultLocale);

    const articleNodes = await deserialize(articleResponse?.data) as DrupalNode[] ?? [];
    setArticleResultCount(articleResponse?.data?.meta?.count ?? 0);
    setArticleResults(articleNodes);

    const productNodes = await deserialize(productResponse?.data) as DrupalProductNode[] ?? [];
    setProductResultCount(productResponse?.data?.meta?.count ?? 0);
    setProductResults(productNodes);

    const eventNodes = await deserialize(eventResponse?.data) as DrupalProductNode[] ?? [];
    setEventResultCount(eventResponse?.data?.meta?.count ?? 0);
    setEventResults(eventNodes);

    const landingPageNodes = await deserialize(landingPageResponse?.data) as DrupalProductNode[] ?? [];
    for (const node of landingPageNodes) {
      // Get a suitable image and description for the landing page, as the image has to come from the hero or an image
      // block, and the description from the hero or a wysiwyg element.
      if (node?.field_paragraphs?.length) {
        const paragraphs: {[key: string]: DrupalParagraph} = {};
        for (const paragraph of node.field_paragraphs) {
          if (paragraph?.type === "paragraph--image_block" && !paragraphs?.image_block) paragraphs.image_block = paragraph;
          if (paragraph?.type === "paragraph--hero" && !paragraphs?.hero) paragraphs.hero = paragraph;
          if (paragraph?.type === "paragraph--wysiwyg" && !paragraphs?.wysiwyg) paragraphs.wysiwyg = paragraph;
          if (paragraphs?.image_block && paragraphs?.hero && paragraphs?.wysiwyg) break;
        }
        if (!node?.hero) {
          node.image = paragraphs?.hero?.field_hero_image;
          node.description = paragraphs?.hero?.field_hero_text;
        }
        if (!node.image) {
          node.image = paragraphs?.image_block?.field_image_block_image;
        }
        if (!node.description) {
          node.description = paragraphs?.wysiwyg?.field_body;
        }
      }
    }
    setLandingPageResultCount(landingPageResponse?.data?.meta?.count ?? 0);
    setLandingPageResults(landingPageNodes);
    setLoading(false);
  }

  async function searchCallback(searchText: string) {
    if (searchText) {
      const path = router.asPath.split("?")[0];
      setProductPage(0);
      setArticlePage(0);
      setEventPage(0);
      setLandingPagePage(0);
      setLoading(true);
      await router.replace(
        {pathname: path, query: {s: searchText}},
        undefined,
        {
          shallow: true,
        },
      );
    }
  }

  useEffect(() => {
    if (router?.query?.s) {
      // Debounce search.
      const handler = setTimeout(() => {
        setLoading(true);
        updateSearch(router?.query?.s).then(() => {});
      }, 200);
      return () => clearTimeout(handler);
    }
  }, [router?.query, articlePage, eventPage, productPage, landingPagePage]);

 // @todo clean up below and create teaser items for each item below, import using dynamic.
  return (
    <>
      <SearchHero title={node?.title} image={node?.field_kvp_hero_image} small={true} headline={heroHeadline} searchPlaceholder={searchPlaceholder} searchCallback={searchCallback}/>
      {router?.query?.s && (
        <div className="container mx-auto text-center pb-12">
          {loading ? (
            <LoadingSpinnerOverlay fixed={true} loading={loading}/>
          ) : (
            <>
              <h2 className="!font-semibold">{resultsForText} “{router.query.s}”</h2>
              <div className="pt-2 text-2xl font-light">
                {articleResultCount + productResultCount + eventResultCount + landingPageResultCount} {resultsFoundText}
              </div>
              <div className="pt-4 font-light">
                {t.search.document_results} <Link className="text-primary" href="/document-library"> {t.search.document_library}</Link>  {t.search.instead}.
              </div>
            </>
          )}
        </div>
      )}
      <div className="container px-6 pb-10 mx-auto flex gap-4 justify-end">
        {productResultCount ?
          <Button callback={() => scrollToRef("search-heading--products")} invert={true} color={"black"} hover={{color: "primary"}}>{productsHeaderText}</Button> : <></>}
        {landingPageResultCount ? <Button callback={() => scrollToRef("search-heading--landing-page")} invert={true} color={"black"} hover={{color: "primary"}}>{landingPageHeaderText}</Button> : <></>}
        {articleResultCount ? <Button callback={() => scrollToRef("search-heading--articles")} invert={true} color={"black"} hover={{color: "primary"}}>{newsHeaderText}</Button> : <></>}
        {eventResultCount ? <Button callback={() => scrollToRef("search-heading--events")} invert={true} color={"black"} hover={{color: "primary"}}>{eventsHeaderText}</Button> : <></>}
      </div>
      <div className="container px-6 pb-10 mx-auto scroll-m-20" ref={scrollRef}>
        <div id="general-search-form" className="flex flex-col">
          {/* Products */}
          <>
            <div className="w-full flex flex-col gap-4">
              <div className="border-b-gray-300 border-b pb-4 mb-4 flex items-baseline">
                <h2 id="search-heading--products" className="md:!text-4xl font-light scroll-m-20">
                  {productsHeaderText}
                </h2>
                {/* @todo Localize */}
                <div className="text-primary pl-4">{productResultCount} {productResultCount > 1 ? resultsText : singleResultText}</div>
              </div>
              {productResults && productResults.length ? (productResults.map((item) => (
                  <div key={item.id}>
                    {item?.path?.alias && (
                      <Link
                        href={item?.path?.alias ?? "/"}
                        className="block overflow-hidden no-underline rounded-md bg-white p-4 drop-shadow-sm hover:drop-shadow-md hover:text-primary transition-all duration-500"
                      >
                        <div className="text-lg font-bold flex items-start gap-8 flex-col md:flex-row max-md:items-center">
                          {item?.field_product_image?.length ? (
                              <MediaImage
                                media={item?.field_product_image?.[0]}
                                priority
                                width={160}
                                height={80}
                                sizes="512px"
                                style={{height: "80px", width: "160px", minWidth: "160px", objectFit: "contain", objectPosition: "center top"}}
                              />
                          ): <></>}
                          <div>
                            <h4 className="md:!text-xl">
                              {item?.field_product_brand?.name ? (
                                <>{item?.field_product_brand?.name ?? ""}{item?.field_product_brand?.field_brand_suffix ?? ""} {item?.title}</>
                              ): <>{item.title}</>}
                            </h4>
                            {item?.body?.summary && (
                              <p data-cy="summary" className="font-light text-black">
                                {item?.body?.summary.slice(0, 150)}...
                              </p>
                            )}
                            {!item?.body?.summary && item?.body?.processed && (
                              <p data-cy="summary" className="font-light text-black [&>p]:inline">
                                {item?.field_product_vars?.[0]?.field_var_number?.length  && item?.field_product_vars?.[0]?.field_var_number[0] !== '-'
                                  ? (
                                    <>
                                      {item.field_product_vars.map((variation, index) => (
                                        <span key={`${item.id}-product-numbers-${index}`}>{variation.field_var_number}{ index < item.field_product_vars.length -1 && ', '}</span>
                                      ))}
                                      <span> - </span>
                                    </>
                                  )
                                : <></>}
                                <FormattedText processed={`${item?.body?.processed.slice(0, 150)}...`} />
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                )))
                : <>{t?.search?.no_products}</>}
            </div>
            <div className="mb-10">
              <Pagination count={productResultCount} page={productPage} limit={pageLimit} changePage={setProductPage} radius={8} scrollRef="search-heading--products"/>
            </div>
          </>

          {/* Landing Pages */}
          <>
            <div className="w-full flex flex-col gap-4">
              <div className="border-b-gray-300 border-b pb-4 mb-4 flex items-baseline">
                <h1 id="search-heading--landing-page" className="md:!text-4xl font-light scroll-m-20">
                  {landingPageHeaderText}
                </h1>
                <div className="text-primary pl-4">{landingPageResultCount} {landingPageResultCount > 1 ? resultsText : singleResultText}</div>
              </div>
              {landingPageResults && landingPageResults.length ? (landingPageResults.map((item) => (
                  <div key={item.id}>
                    {item?.path?.alias && (
                      <Link
                        href={item?.path?.alias ?? "/"}
                        className="block overflow-hidden no-underline rounded-md bg-white p-4 drop-shadow-sm hover:drop-shadow-md hover:text-primary transition-all duration-500"
                      >
                        <div className="text-lg font-bold flex items-start gap-8 flex-col md:flex-row max-md:items-center">
                          {item?.image ? (
                            <MediaImage
                              media={item.image}
                              priority
                              width={160}
                              height={80}
                              sizes="10vw"
                              style={{height: "80px", width: "160px", minWidth: "160px", objectFit: "cover", objectPosition: "center"}}
                            />
                          ): <></>}
                          <div>
                            <h4 className="md:!text-xl">
                              {item?.title}
                            </h4>
                            {item?.description?.processed && (
                              <p data-cy="summary" className="font-light text-black">
                                <TruncateMarkup html={item.description.processed} length={150} suffix={"..."}/>
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                )))
                : <>{t?.search?.no_pages}</>}
            </div>
            <div className="mb-10">
              <Pagination count={landingPageResultCount} page={landingPagePage} limit={pageLimit} changePage={setLandingPagePage} radius={8} scrollRef="search-heading--landing-page"/>
            </div>
          </>

          {/* Articles */}
          <>
            <div className="w-full flex flex-col gap-4">
              <div className="border-b-gray-300 border-b pb-4 mb-4 flex items-baseline">
                <h1 id="search-heading--articles" className="md:!text-4xl font-light scroll-m-20">
                  {newsHeaderText}
                </h1>
                <div className="text-primary pl-4">{articleResultCount} {articleResultCount > 1 ? resultsText : singleResultText}</div>
              </div>
              {articleResults && articleResults.length ? (articleResults.map((item) => (
                  <div key={item.id}>
                    {item?.path?.alias && (
                      <Link
                        href={item?.path?.alias ?? "/"}
                        className="block overflow-hidden no-underline rounded-md bg-white p-4 drop-shadow-sm hover:drop-shadow-md hover:text-primary transition-all duration-500"
                      >
                        <div className="text-lg font-bold flex items-start gap-8 flex-col md:flex-row max-md:items-center">
                          <MediaImage
                            media={item?.field_article_image}
                            priority
                            width={160}
                            height={80}
                            sizes="10vw"
                            style={{height: "80px", width: "160px", minWidth: "160px", objectFit: "cover", objectPosition: "center top"}}
                          />
                        <div>
                            <h4 className="md:!text-xl">
                              {item?.title}
                            </h4>
                            {item?.body?.summary && (
                              <p data-cy="summary" className="font-light text-black">
                                {item?.body?.summary.slice(0, 150)}...
                              </p>
                            )}
                            {!item?.body?.summary && item?.body?.processed && (
                              <p data-cy="summary" className="font-light text-black">
                                <FormattedText processed={`${striptags(item?.body?.processed.slice(0, 150))}...`} />
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                )))
                : <>{t?.search?.no_articles}</>}
            </div>
            <div className="mb-10">
              <Pagination count={articleResultCount} page={articlePage} limit={pageLimit} changePage={setArticlePage} scrollRef="search-heading--articles" radius={8}/>
            </div>
          </>

          {/* Events */}
          <>
            <div className="w-full flex flex-col gap-4">
              <div className="border-b-gray-300 border-b pb-4 mb-4 flex items-baseline">
                <h1 id="search-heading--events" className="md:!text-4xl font-light scroll-m-20">
                  {eventsHeaderText}
                </h1>
                <div className="text-primary pl-4">{eventResultCount} {eventResultCount > 1 ? resultsText : singleResultText}</div>
              </div>
              {eventResults && eventResults.length ? (eventResults.map((item) => (
                  <div key={item.id}>
                    {item?.path?.alias && (
                      <Link
                        href={item?.path?.alias ?? "/"}
                        className="block overflow-hidden no-underline rounded-md bg-white p-4 drop-shadow-sm hover:drop-shadow-md hover:text-primary transition-all duration-500"
                      >
                        <div className="text-lg font-bold flex items-start gap-8 flex-col md:flex-row max-md:items-center">
                          <MediaImage
                            media={item?.field_event_hero_image || item?.field_event_image}
                            priority
                            width={160}
                            height={80}
                            sizes="10vw"
                            style={{height: "80px", width: "160px", minWidth: "160px", objectFit: "cover", objectPosition: "center"}}
                          />
                          <div>
                            <h4 className="md:!text-xl">
                              {item?.title}
                            </h4>
                            {item?.body?.summary && (
                              <p data-cy="summary" className="font-light text-black">
                                {item?.body?.summary.slice(0, 150)}...
                              </p>
                            )}
                            {!item?.body?.summary && item?.body?.processed && (
                              <p data-cy="summary" className="font-light text-black">
                                <FormattedText processed={`${item?.body?.processed.slice(0, 150)}...`} />
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                )))
                : <>{t?.search?.no_events}</>}
            </div>
            <div>
              <Pagination count={eventResultCount} page={eventPage} limit={pageLimit} changePage={setEventPage} radius={8} scrollRef="search-heading--events"/>
            </div>
          </>
        </div>
      </div>
    </>
  );
}
